import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiX, FiUpload, FiAlertCircle } from 'react-icons/fi';
import { AdminLayout } from '../../components/layout';
import { Card, Button, Loading } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { createCar, getCarById, updateCar } from '../../firebase/cars';
import { getAllCompanies } from '../../firebase/companies';
import { uploadCarImages } from '../../firebase/storage';

const FUEL_TYPES = ['Petrol', 'Diesel', 'Electric', 'Hybrid', 'CNG'];
const TRANSMISSION_TYPES = ['Automatic', 'Manual'];
const CAR_SUBTYPES = ['Sedan', 'SUV', 'Hatchback', 'MUV', 'Luxury', 'Convertible', 'Pickup', 'Van'];
const COMMON_FEATURES = [
  'AC', 'GPS Navigation', 'Bluetooth', 'USB Charging', 'Sunroof', 
  'Leather Seats', 'Cruise Control', 'Backup Camera', 'Airbags',
  'ABS', 'Parking Sensors', 'Keyless Entry', 'Push Start',
  'Power Windows', 'Power Steering', 'Music System', 'Touchscreen'
];

const AdminCarAdd = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get car ID from URL params
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(!!id); // Show loading if editing
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]); // For existing car images
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    model: '',
    subType: '',
    year: new Date().getFullYear(),
    color: '',
    seats: 5,
    transmission: 'Manual',
    fuelType: 'Petrol',
    mileage: '',
    odometer: '',
    registrationNumber: '',
    features: [],
    description: '',
    price12Hour: '',
    price24Hour: '',
    minBookingHours: 6,
    maxBookingHours: 720,
    hiddenFields: [],
    status: 'active'
  });

  const [errors, setErrors] = useState({});

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Load car data if in edit mode
  useEffect(() => {
    if (id) {
      loadCarData();
    }
  }, [id]);

  const loadCarData = async () => {
    try {
      setPageLoading(true);
      const result = await getCarById(id);
      if (result.car) {
        const car = result.car;
        
        // Set form data
        setFormData({
          name: car.name || '',
          brand: car.brand || '',
          model: car.model || '',
          subType: car.subType || '',
          year: car.year || new Date().getFullYear(),
          color: car.color || '',
          seats: car.seats || 5,
          transmission: car.transmission || 'Manual',
          fuelType: car.fuelType || 'Petrol',
          mileage: car.mileage || '',
          odometer: car.odometer || '',
          registrationNumber: car.registrationNumber || '',
          features: car.features || [],
          description: car.description || '',
          price12Hour: car.price12Hour || '',
          price24Hour: car.price24Hour || '',
          minBookingHours: car.minBookingHours || 6,
          maxBookingHours: car.maxBookingHours || 720,
          hiddenFields: car.hiddenFields || [],
          status: car.status || 'active'
        });
        
        // Set company ID
        setSelectedCompanyId(car.companyId || '');
        
        // Set existing images
        if (car.images && car.images.length > 0) {
          setExistingImages(car.images);
        }
      }
    } catch (error) {
      console.error('Error loading car:', error);
      setErrors({ load: 'Failed to load car data' });
    } finally {
      setPageLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const result = await getAllCompanies();
      if (result.companies) {
        const activeCompanies = result.companies.filter(c => c.status === 'active');
        setCompanies(activeCompanies);
        if (activeCompanies.length > 0) {
          setSelectedCompanyId(activeCompanies[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const totalExisting = existingImages.length + imageFiles.length;
    const maxFiles = 5 - totalExisting;
    const newFiles = files.slice(0, maxFiles);
    
    if (newFiles.length > 0) {
      setImageFiles(prev => [...prev, ...newFiles]);
      
      newFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handleHiddenFieldToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      hiddenFields: prev.hiddenFields.includes(field)
        ? prev.hiddenFields.filter(f => f !== field)
        : [...prev.hiddenFields, field]
    }));
  };

  const validateForm = (data) => {
    const newErrors = {};
    
    if (!selectedCompanyId) newErrors.company = 'Please select a company';
    if (!data.name || !data.name.trim()) newErrors.name = 'Car name is required';
    if (!data.brand || !data.brand.trim()) newErrors.brand = 'Brand is required';
    if (!data.seats || data.seats < 1) newErrors.seats = 'Valid seat count required';
    if (!data.price12Hour || data.price12Hour <= 0) newErrors.price12Hour = '12-hour price is required';
    if (!data.price24Hour || data.price24Hour <= 0) newErrors.price24Hour = '24-hour price is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get values directly from DOM
    const form = e.target;
    const actualData = {
      name: form.querySelector('input[name="name"]')?.value || formData.name,
      brand: form.querySelector('input[name="brand"]')?.value || formData.brand,
      model: form.querySelector('input[name="model"]')?.value || formData.model,
      color: form.querySelector('input[name="color"]')?.value || formData.color,
      seats: Number(form.querySelector('input[name="seats"]')?.value) || formData.seats,
      price12Hour: Number(form.querySelector('input[name="price12Hour"]')?.value) || formData.price12Hour,
      price24Hour: Number(form.querySelector('input[name="price24Hour"]')?.value) || formData.price24Hour,
      mileage: form.querySelector('input[name="mileage"]')?.value || formData.mileage,
      odometer: Number(form.querySelector('input[name="odometer"]')?.value) || formData.odometer,
      registrationNumber: form.querySelector('input[name="registrationNumber"]')?.value || formData.registrationNumber,
      year: Number(form.querySelector('input[name="year"]')?.value) || formData.year,
      minBookingHours: Number(form.querySelector('input[name="minBookingHours"]')?.value) || formData.minBookingHours,
      maxBookingHours: Number(form.querySelector('input[name="maxBookingHours"]')?.value) || formData.maxBookingHours,
      subType: form.querySelector('select[name="subType"]')?.value || formData.subType,
      transmission: form.querySelector('select[name="transmission"]')?.value || formData.transmission,
      fuelType: form.querySelector('select[name="fuelType"]')?.value || formData.fuelType,
      description: form.querySelector('textarea[name="description"]')?.value || formData.description,
      features: formData.features,
      hiddenFields: formData.hiddenFields,
      status: formData.status
    };
    
    if (!validateForm(actualData)) return;
    
    setLoading(true);
    
    try {
      let imageUrls = [];
      
      // Handle image uploads for new images
      if (imageFiles.length > 0) {
        const tempCarId = isEditMode ? id : Date.now().toString();
        const uploadResult = await uploadCarImages(imageFiles, selectedCompanyId, tempCarId);
        if (uploadResult.urls) {
          imageUrls = uploadResult.urls;
        }
      }
      
      // Combine existing images with newly uploaded ones
      const allImages = isEditMode 
        ? [...existingImages, ...imageUrls]
        : imageUrls;
      
      const carData = {
        ...actualData,
        images: allImages,
        price12Hour: Number(actualData.price12Hour),
        price24Hour: Number(actualData.price24Hour),
        seats: Number(actualData.seats),
        year: actualData.year ? Number(actualData.year) : null,
        odometer: actualData.odometer ? Number(actualData.odometer) : null,
        minBookingHours: Number(actualData.minBookingHours),
        maxBookingHours: Number(actualData.maxBookingHours),
      };
      
      let result;
      if (isEditMode) {
        // Update existing car
        result = await updateCar(id, carData);
      } else {
        // Create new car
        result = await createCar(selectedCompanyId, carData, user?.uid);
      }
      
      if (result.error) {
        setErrors({ submit: result.error });
        setLoading(false);
        return;
      }
      
      navigate('/admin/cars', { 
        state: { message: `Car "${actualData.name}" ${isEditMode ? 'updated' : 'added'} successfully!` }
      });
      
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} car:`, error);
      setErrors({ submit: `Failed to ${isEditMode ? 'update' : 'add'} car. Please try again.` });
      setLoading(false);
    }
  };

  if (companiesLoading || pageLoading) {
    return (
      <AdminLayout title={isEditMode ? "Edit Car" : "Add New Car"}>
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </AdminLayout>
    );
  }

  if (companies.length === 0) {
    return (
      <AdminLayout title={isEditMode ? "Edit Car" : "Add New Car"}>
        <div className="max-w-4xl mx-auto">
          <Card className="p-12 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiAlertCircle className="text-yellow-600" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Companies Available</h3>
            <p className="text-gray-600 mb-4">
              You need to create a company before adding cars.
            </p>
            <Button onClick={() => navigate('/admin/companies/new')}>
              Create Company First
            </Button>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditMode ? "Edit Car" : "Add New Car"}>
      <form onSubmit={handleSubmit}>
        <div className="max-w-4xl mx-auto space-y-6">

        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <FiAlertCircle className="text-red-500 flex-shrink-0" size={20} />
            <p className="text-red-700">{errors.submit}</p>
          </div>
        )}

        {/* Company Selection - IMPORTANT */}
        <Card className="p-6 border-2 border-primary-200 bg-primary-50">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Company *</h3>
          <p className="text-sm text-gray-600 mb-4">
            {isEditMode 
              ? 'Company cannot be changed after car is created.'
              : 'Choose which company this car belongs to. This cannot be changed later.'}
          </p>
          <select
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            disabled={isEditMode}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg ${
              errors.company ? 'border-red-500' : 'border-gray-300'
            } ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          >
            {companies.map(company => (
              <option key={company.id} value={company.id}>
                {company.name} ({company.slug}.carbooking.com)
              </option>
            ))}
          </select>
          {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}
        </Card>

        {/* Images */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Car Images</h3>
          
          {/* Existing Images */}
          {isEditMode && existingImages.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Current Images</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {existingImages.map((imageUrl, index) => (
                  <div key={`existing-${index}`} className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <img src={imageUrl} alt={`Existing ${index + 1}`} className="w-full h-full object-contain bg-gray-100" />
                    <button
                      type="button"
                      onClick={() => removeExistingImage(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* New Images */}
          {imagePreviews.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">New Images to Upload</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {imagePreviews.map((preview, index) => (
                  <div key={`new-${index}`} className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <img src={preview} alt={`New ${index + 1}`} className="w-full h-full object-contain bg-gray-100" />
                    <button
                      type="button"
                      onClick={() => removeNewImage(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Upload Button */}
          {(existingImages.length + imageFiles.length) < 5 && (
            <label className="aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
              <FiUpload size={24} className="text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">Add Image</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                multiple
              />
            </label>
          )}
          
          <p className="text-sm text-gray-500 mt-2">
            Upload up to 5 images total. First image will be the primary image.
            {isEditMode && ' Click X to remove unwanted images.'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Recommended: 1280 × 720 px (16:9 ratio). Max file size: 5MB per image.
          </p>
        </Card>

        {/* Basic Information */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Car Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Toyota Fortuner"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brand *</label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                placeholder="e.g., Toyota"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.brand ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.brand && <p className="text-red-500 text-sm mt-1">{errors.brand}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                placeholder="e.g., Fortuner"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                name="subType"
                value={formData.subType}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select type</option>
                {CAR_SUBTYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input
                type="number"
                name="year"
                value={formData.year}
                onChange={handleChange}
                min="1990"
                max={new Date().getFullYear() + 1}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleChange}
                placeholder="e.g., Pearl White"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </Card>

        {/* Specifications */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Specifications</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of Seats *</label>
              <input
                type="number"
                name="seats"
                value={formData.seats}
                onChange={handleChange}
                min="1"
                max="50"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.seats ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.seats && <p className="text-red-500 text-sm mt-1">{errors.seats}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transmission</label>
              <select
                name="transmission"
                value={formData.transmission}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {TRANSMISSION_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Type</label>
              <select
                name="fuelType"
                value={formData.fuelType}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {FUEL_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mileage</label>
              <input
                type="text"
                name="mileage"
                value={formData.mileage}
                onChange={handleChange}
                placeholder="e.g., 15 kmpl"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Odometer (km)</label>
              <input
                type="number"
                name="odometer"
                value={formData.odometer}
                onChange={handleChange}
                placeholder="e.g., 25000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
              <input
                type="text"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleChange}
                placeholder="e.g., MH01AB1234"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </Card>

        {/* Features */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Features</h3>
          
          <div className="flex flex-wrap gap-2">
            {COMMON_FEATURES.map(feature => (
              <button
                key={feature}
                type="button"
                onClick={() => handleFeatureToggle(feature)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  formData.features.includes(feature)
                    ? 'bg-primary-100 text-primary-700 border border-primary-200'
                    : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                }`}
              >
                {feature}
              </button>
            ))}
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Additional details about the car..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </Card>

        {/* Pricing */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">12-Hour Rental Price (₹) *</label>
              <input
                type="number"
                name="price12Hour"
                value={formData.price12Hour}
                onChange={handleChange}
                min="0"
                placeholder="e.g., 1500"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.price12Hour ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.price12Hour && <p className="text-red-500 text-sm mt-1">{errors.price12Hour}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">24-Hour Rental Price (₹) *</label>
              <input
                type="number"
                name="price24Hour"
                value={formData.price24Hour}
                onChange={handleChange}
                min="0"
                placeholder="e.g., 2500"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.price24Hour ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.price24Hour && <p className="text-red-500 text-sm mt-1">{errors.price24Hour}</p>}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Booking (hours)</label>
              <input
                type="number"
                name="minBookingHours"
                value={formData.minBookingHours}
                onChange={handleChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Booking (hours)</label>
              <input
                type="number"
                name="maxBookingHours"
                value={formData.maxBookingHours}
                onChange={handleChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </Card>

        {/* Visibility Controls */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Visibility Settings</h3>
          <p className="text-sm text-gray-500 mb-4">Select fields to hide from customers</p>
          
          <div className="flex flex-wrap gap-2">
            {['odometer', 'registrationNumber', 'mileage', 'year'].map(field => (
              <button
                key={field}
                type="button"
                onClick={() => handleHiddenFieldToggle(field)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  formData.hiddenFields.includes(field)
                    ? 'bg-red-100 text-red-700 border border-red-200'
                    : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                }`}
              >
                {field === 'registrationNumber' ? 'Registration' : field.charAt(0).toUpperCase() + field.slice(1)}
                {formData.hiddenFields.includes(field) && ' (Hidden)'}
              </button>
            ))}
          </div>
        </Card>

        {/* Status */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="status"
                value="active"
                checked={formData.status === 'active'}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600"
              />
              <span className="text-gray-700">Active (Available for booking)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="status"
                value="maintenance"
                checked={formData.status === 'maintenance'}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600"
              />
              <span className="text-gray-700">Under Maintenance</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="status"
                value="inactive"
                checked={formData.status === 'inactive'}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600"
              />
              <span className="text-gray-700">Inactive</span>
            </label>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/cars')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2"
          >
            {loading ? <Loading size="sm" /> : <FiSave size={18} />}
            {isEditMode ? 'Update Car' : 'Add Car'}
          </Button>
        </div>
        </div>
      </form>
    </AdminLayout>
  );
};

export default AdminCarAdd;
